import React from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { SentryErrorFallBack } from './components/SentryErrorFallback/SentryErrorFallBack';

const tracePropagationTargets: Array<string | RegExp> = [
  'localhost',
  /^\//,
  /^https:\/\/.*\.eximb\.com\//,
];

if (import.meta.env.VITE_AXIOS_IDENTIFICATION_BASE_URL) {
  tracePropagationTargets.push(import.meta.env.VITE_AXIOS_IDENTIFICATION_BASE_URL);
}

let isSentryInitialized = false;

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export function initializeSentry(): void {
  if (isSentryInitialized) {
    return;
  }

  isSentryInitialized = true;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    environment: import.meta.env.VITE_SENTRY_APP_ENV,
    enableLogs: true,
    attachStacktrace: true,
    tracePropagationTargets,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn', 'assert'],
      }),
      Sentry.extraErrorDataIntegration({
        depth: 5,
        captureErrorCause: true,
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [400, [402, 599]],
        failedRequestTargets: [/.*/],
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
        networkCaptureBodies: true,
        networkDetailAllowUrls: ['https://portal-t.eximb.com', 'https://portal.eximb.com'],
      }),
    ],
    tracesSampleRate: 1.0,
    sampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
  Sentry.setTag('app', 'web-portal');
}

export function SentryAppBoundary({ children }: { children: React.ReactNode }) {
  return <Sentry.ErrorBoundary fallback={SentryErrorFallBack}>{children}</Sentry.ErrorBoundary>;
}
